import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { PatientCensus, PatientCensusService } from "@/api";

type CensusState = {
  census: PatientCensus[] | null;
};

const state: CensusState = {
  census: null,
};

const getters: GetterTree<CensusState, RootState> = {
  census: state => state.census,
};

const mutations: MutationTree<CensusState> = {
  SET_CENSUS(state, census: PatientCensus[]) {
    state.census = census;
  },
  UPDATE_CENSUS(state, census: PatientCensus) {
    if (!state.census) return;
    const patient = state.census.find(p => p.id === census.id);
    if (patient) Object.assign(patient, census);
  },
  ADD_CENSUS(state, patient: PatientCensus) {
    if (!state.census) state.census = [];
    state.census.push(patient);
  },
};

const actions: ActionTree<CensusState, RootState> = {
  async getCensus(
    { commit, rootState },
    payload?: { start?: string; end?: string },
  ) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }

    try {
      const census = await PatientCensusService.getPatientCensus({
        companyId,
        start: payload?.start,
        end: payload?.end,
      });
      commit("SET_CENSUS", census);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async addCensus({ commit, rootState }, census: PatientCensus) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }
    try {
      // eslint-disable-next-line @typescript-eslint/camelcase
      census.company_id = companyId;
      const newCensus = await PatientCensusService.addPatientCensus({
        companyId,
        requestBody: census,
      });
      commit("ADD_CENSUS_PATIENT", newCensus);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  async updateCensus({ commit, rootState }, census: PatientCensus) {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      throw new Error("Company ID is not available in the state.");
    }
    try {
      // eslint-disable-next-line @typescript-eslint/camelcase
      census.company_id = companyId;
      const updatedCensus = await PatientCensusService.updatePatientCensus({
        companyId,
        requestBody: census,
      });
      commit("UPDATE_CENSUS_STATUS", updatedCensus);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const census: Module<CensusState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
